import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  HistoryToggleOff,
  AutoAwesome,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { arabicColumnLabel, arabicEntityLabel } from '../lib/columnLabels';
import { stripHtml } from '../lib/htmlUtils';

interface AuditLog {
  id: number;
  table_name: string;
  record_id: string | null;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  actor_email: string | null;
  source: string | null; // 'admin' | 'agent'
  changed_fields: string[] | null;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  created_at: string;
}

const OP_META: Record<AuditLog['operation'], { color: 'success' | 'info' | 'error'; icon: React.ReactElement }> = {
  INSERT: { color: 'success', icon: <Add /> },
  UPDATE: { color: 'info', icon: <Edit /> },
  DELETE: { color: 'error', icon: <Delete /> },
};

// Tables offered in the filter dropdown (audited business/content tables).
const FILTER_TABLES = [
  'news', 'gallery_photos', 'photo_albums', 'board_meetings', 'board_attachments',
  'branches', 'related_links', 'info_pages', 'events', 'trainings', 'tenders',
  'contracts', 'companies', 'partners', 'classifications', 'messages',
  'outward_letters', 'fund_system', 'documents', 'document_templates', 'areas', 'cities',
];

const PAGE = 20;
const ISO_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})/;

// Pick a human-friendly label for the affected record.
const recordLabel = (log: AuditLog): string => {
  const d = log.new_data || log.old_data || {};
  const candidate =
    d.title || d.name || d.tender_name || d.meeting_no || d.label ||
    d.page || d.filename || d.subject || d.email || d.pcu_id;
  if (candidate) return String(candidate);
  return log.record_id ? `#${log.record_id}` : '—';
};

interface Props {
  title?: string;
  limit?: number;
  // Dense mode for embedding on the Dashboard: small fixed count, no filter
  // dropdown, no per-row diff expand, and a single "view all" link to the
  // full Activity Log page instead of inline "load more" pagination.
  compact?: boolean;
}

const RecentActivity: React.FC<Props> = ({ title, limit, compact = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-US';
  const effectiveLimit = limit ?? (compact ? 4 : PAGE);

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [table, setTable] = useState('');
  const [hideSystem, setHideSystem] = useState(true);
  const [count, setLimitCount] = useState(effectiveLimit);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (tbl: string, hideSys: boolean, lim: number, more: boolean) => {
    more ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
      let q = supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .range(0, lim); // fetch one extra to detect "has more"
      if (tbl) q = q.eq('table_name', tbl);
      // Hide changes with no signed-in user (imports, direct DB edits) and
      // download-counter bumps, which are not real edits.
      if (hideSys) q = q.not('actor_id', 'is', null).neq('changed_fields', '{download_count}');
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data || []) as AuditLog[];
      setHasMore(rows.length > lim);
      setLogs(rows.slice(0, lim));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(table, hideSystem, count, false);
  }, [fetchLogs, table, hideSystem, count]);

  const fmtVal = (v: any): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? t('common.yes') || 'Yes' : t('common.no') || 'No';
    if (typeof v === 'object') {
      const s = JSON.stringify(v);
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    }
    let s = String(v);
    if (ISO_RE.test(s)) {
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d.toLocaleString(locale);
    }
    if (/<\/?[a-z][\s\S]*>/i.test(s)) s = stripHtml(s);
    return s.length > 100 ? s.slice(0, 100) + '…' : s;
  };

  const verb = (op: AuditLog['operation']) =>
    op === 'INSERT' ? t('activity.created')
      : op === 'UPDATE' ? t('activity.updated')
        : t('activity.deleted');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <HistoryToggleOff sx={{ fontSize: compact ? 18 : 20, color: 'text.secondary' }} />
        <Typography variant={compact ? 'subtitle2' : 'h6'} sx={{ flex: 1, fontWeight: compact ? 700 : undefined }}>
          {title || t('activity.title')}
        </Typography>
        {!compact && (
        <FormControlLabel
          control={<Checkbox size="small" checked={hideSystem} onChange={(e) => { setLimitCount(effectiveLimit); setHideSystem(e.target.checked); }} />}
          label={<Typography variant="body2">{t('activity.hideSystem')}</Typography>}
        />
        )}
        {!compact && (
        <TextField
          select
          size="small"
          value={table}
          onChange={(e) => { setLimitCount(effectiveLimit); setTable(e.target.value); }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('activity.allTables')}</MenuItem>
          {FILTER_TABLES.map((tbl) => (
            <MenuItem key={tbl} value={tbl}>{arabicEntityLabel(tbl)}</MenuItem>
          ))}
        </TextField>
        )}
      </Box>
      <Divider sx={{ mb: 1 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : error ? (
        <Typography color="error" variant="body2" sx={{ py: 2 }}>{error}</Typography>
      ) : logs.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 4, textAlign: 'center' }}>
          {t('activity.noActivity')}
        </Typography>
      ) : (
        <>
          <List disablePadding>
            {logs.map((log) => {
              const meta = OP_META[log.operation];
              const isOpen = expanded === log.id;
              const canExpand = !compact && log.operation === 'UPDATE' && !!log.changed_fields?.length;
              return (
                <React.Fragment key={log.id}>
                  <ListItem
                    sx={{ px: 0, py: compact ? 0.375 : 0.75, alignItems: 'flex-start' }}
                    secondaryAction={
                      canExpand ? (
                        <IconButton edge="end" size="small" onClick={() => setExpanded(isOpen ? null : log.id)}>
                          {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      ) : undefined
                    }
                  >
                    <ListItemAvatar sx={{ minWidth: compact ? 34 : 44 }}>
                      <Tooltip title={log.operation}>
                        <Avatar sx={{ bgcolor: (th) => th.palette[meta.color].main, width: compact ? 24 : 32, height: compact ? 24 : 32 }}>
                          {React.cloneElement(meta.icon, { sx: { fontSize: compact ? 14 : 18 } })}
                        </Avatar>
                      </Tooltip>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600, fontSize: compact ? '0.8rem' : undefined }}>
                            {arabicEntityLabel(log.table_name)}
                          </Typography>
                          <Chip label={verb(log.operation)} size="small" color={meta.color}
                            sx={{ height: compact ? 17 : 20, fontSize: compact ? '0.65rem' : '0.7rem' }} />
                          {log.source === 'agent' && (
                            <Tooltip title={t('activity.byAgentTip')}>
                              <Chip label={t('activity.byAgent')} size="small" color="secondary" variant="outlined"
                                icon={<AutoAwesome sx={{ fontSize: 13 }} />}
                                sx={{ height: compact ? 17 : 20, fontSize: compact ? '0.62rem' : '0.68rem', '& .MuiChip-icon': { ml: 0.5 } }} />
                            </Tooltip>
                          )}
                          <Typography variant="body2" component="span" color="text.secondary" noWrap
                            sx={{ maxWidth: compact ? 160 : 260, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: compact ? '0.78rem' : undefined }}>
                            {recordLabel(log)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.68rem' : undefined }}>
                          {(log.actor_email || t('activity.system'))} · {new Date(log.created_at).toLocaleString(locale)}
                          {!compact && log.operation === 'UPDATE' && log.changed_fields?.length
                            ? ` · ${log.changed_fields.map(arabicColumnLabel).join('، ')}`
                            : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {canExpand && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 5.5, pr: 2, pb: 1 }}>
                        <Table size="small" sx={{ '& td, & th': { borderBottom: 'none', py: 0.25, px: 1 } }}>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '30%' }}>
                                {t('activity.field')}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('activity.old')}</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('activity.new')}</TableCell>
                            </TableRow>
                            {log.changed_fields!.map((f) => (
                              <TableRow key={f}>
                                <TableCell sx={{ fontWeight: 500 }}>{arabicColumnLabel(f)}</TableCell>
                                <TableCell sx={{ color: 'error.main', wordBreak: 'break-word' }}>
                                  {fmtVal(log.old_data?.[f])}
                                </TableCell>
                                <TableCell sx={{ color: 'success.main', wordBreak: 'break-word' }}>
                                  {fmtVal(log.new_data?.[f])}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  )}
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
          {compact ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => navigate('/activity')}>
                {t('activity.viewAll')}
              </Button>
            </Box>
          ) : hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
              <Button size="small" onClick={() => setLimitCount((c) => c + PAGE)} disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}>
                {t('activity.loadMore')}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default RecentActivity;
